// Connections — self-serve integration onboarding.
//
// The owner connects their own data sources here instead of running CLI
// scripts + editing .env.local. Each provider is a tile driven by the
// `connections` table (migration 0034, owner RLS). Connecting a credential
// goes through the `connections` edge function, which validates it, stores
// it in Supabase Vault (never in the browser/DB), and records non-secret
// state here.
//
// Phase 1: Fathom is fully wired (paste API key → validate → backfill every
// meeting). The remaining providers are shown as upcoming so the page
// reflects the full capture surface; they light up as their OAuth flows land.
//
// N1: nothing here sends/posts on the user's behalf — connecting only grants
// read access (+ Gmail drafts, when that provider lands).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Video,
  Mail,
  MessageSquare,
  Smartphone,
  Wallet,
  Loader2,
  Plug,
  RefreshCw,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Copy,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  readAllHeartbeats,
  classifyHeartbeat,
  type HeartbeatPayload,
} from '@reverra/shared/heartbeat/read';
import GlassCard from '@/components/shared/GlassCard';
import PulseDot from '@/components/shared/PulseDot';
import SetupChecklist from '@/components/connections/SetupChecklist';
import BridgeTile from '@/components/connections/BridgeTile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

type Provider = 'fathom' | 'google' | 'discord' | 'gusto' | 'bridge';

type ConnectionRow = {
  provider: Provider;
  status: 'disconnected' | 'connected' | 'error' | 'syncing';
  account_label: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  metadata: { whatsapp_qr?: string | null } | null;
};

type InputField = { label: string; placeholder: string; help?: { text: string; href: string } };
type PasteConnect = { type: 'paste' } & InputField;
type OAuthConnect = { type: 'oauth'; fn: string };

type ProviderMeta = {
  provider: Provider;
  name: string;
  icon: typeof Video;
  blurb: string;
  /** How the user connects. Absent = previewed ("coming soon"). */
  connect?: PasteConnect | OAuthConnect;
  /** Numbered "what you'll do" walkthrough, shown in an expandable on the tile. */
  steps?: string[];
  /** Overrides the "Sync pending…" line on a connected tile when no daemon sets
   *  last_sync_at for this provider yet (e.g. Gusto's roster sync is a later phase). */
  connectedHint?: string;
};

const PROVIDERS: ProviderMeta[] = [
  {
    provider: 'fathom',
    name: 'Fathom',
    icon: Video,
    blurb: 'Pull every recorded meeting — transcript, summary, action items, participants.',
    connect: {
      type: 'paste',
      label: 'Fathom API key',
      placeholder: 'paste your Fathom API key',
      help: {
        text: 'Find it in Fathom → Settings → Integrations → API (Team plan)',
        href: 'https://fathom.video/settings/api',
      },
    },
    steps: [
      'In Fathom, open Settings → Integrations → API and copy your API key (requires a Fathom Team plan).',
      'Paste the key above and click Connect.',
      'Reverra checks the key with Fathom, then pulls in all your past meetings — transcripts, summaries, action items.',
      'New recordings flow in automatically from then on.',
    ],
  },
  {
    provider: 'google',
    name: 'Gmail & Calendar',
    icon: Mail,
    blurb:
      'One Google sign-in captures inbound mail (drafts only — never sends) and your calendar.',
    connect: { type: 'oauth', fn: 'google-oauth' },
    steps: [
      'Click Connect — you’ll be taken to Google’s sign-in.',
      'Choose the Google account whose Gmail + Calendar you want Reverra to capture.',
      'On the consent screen, review the access — read your email & calendar, and save replies to Drafts. Reverra never sends email on your behalf — then click Allow.',
      'You’ll land back here. Gmail and Calendar start syncing automatically.',
      'If you see “Access blocked / app not verified”, your account must be added as a Test user on the Google project first (one-time admin step — see the setup runbook).',
    ],
  },
  {
    provider: 'discord',
    name: 'Discord',
    icon: MessageSquare,
    blurb: 'Get approvals and daily digests in your server. Notifications only.',
    connect: { type: 'oauth', fn: 'discord-oauth' },
    steps: [
      'Click Connect — you’ll go to Discord’s “authorize” screen.',
      'Pick the server to add Reverra to (you need the “Manage Server” permission there). Create one first if you don’t have it.',
      'Click Authorize. Reverra joins with permission only to post in channels — it never reads your messages or sends DMs.',
      'You’ll return here connected; approval cards and the daily digest post to your server.',
    ],
  },
  {
    provider: 'gusto',
    name: 'Gusto',
    icon: Wallet,
    blurb:
      'Read your contractor roster + pay history to line up against the commission export. Read-only — Reverra never runs payroll.',
    connect: { type: 'oauth', fn: 'gusto-oauth' },
    steps: [
      'Click Connect — you’ll be taken to Gusto’s sign-in.',
      'Sign in as a Gusto admin (only a primary or full-access admin can authorize) and pick your company.',
      'Review the read access — your contractors and pay history. Reverra never runs payroll or pays anyone; the actual payroll run stays a manual step you do in Gusto.',
      'You’ll land back here connected. Reverra can then match contractor pay to your commission export.',
    ],
    connectedHint: 'Connected — contractor sync arrives in a later phase',
  },
  {
    provider: 'bridge',
    name: 'iMessage & WhatsApp',
    icon: Smartphone,
    blurb: 'Capture messages via the MacBook bridge. Read-only.',
  },
];

async function fetchConnections(): Promise<Record<string, ConnectionRow>> {
  const { data, error } = await supabase
    .from('connections')
    .select('provider, status, account_label, last_sync_at, last_error, metadata');
  if (error) throw error;
  const byProvider: Record<string, ConnectionRow> = {};
  for (const row of (data ?? []) as ConnectionRow[]) byProvider[row.provider] = row;
  return byProvider;
}

/**
 * Call the `connections` edge function and normalize its error body. supabase-js
 * surfaces non-2xx as a FunctionsHttpError with the JSON body on error.context,
 * so we dig the user-facing `message` out of there.
 */
async function invokeConnections(body: {
  action: 'connect' | 'disconnect' | 'sync';
  provider: Provider;
  secret?: string;
}): Promise<{ account?: string | null }> {
  const { data, error } = await supabase.functions.invoke('connections', { body });
  if (error) {
    let message = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        if (parsed?.message) message = parsed.message;
        else if (parsed?.error) message = parsed.error;
      }
    } catch {
      /* fall back to error.message */
    }
    throw new Error(message);
  }
  return data ?? {};
}

/**
 * Kick off an OAuth provider connect: ask its edge function for the consent
 * URL, then send the browser to it. The function's callback finishes the
 * connection and redirects back to /connections?<provider>=connected.
 */
async function startOAuth(fn: string, extra?: Record<string, unknown>): Promise<void> {
  const { data, error } = await supabase.functions.invoke(fn, {
    body: { action: 'auth_url', ...extra },
  });
  if (error) {
    let message = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        message = parsed?.message ?? parsed?.error ?? message;
      }
    } catch {
      /* fall back */
    }
    throw new Error(message);
  }
  const url = (data as { url?: string })?.url;
  if (!url) throw new Error('No authorization URL returned.');
  window.location.assign(url);
}

// ── Google (per-inbox OAuth) ────────────────────────────────────────────────

// The exact redirect URI the user must register in their OAuth client (guide
// step 5). It is our google-oauth callback on this deployment's Supabase.
const GOOGLE_REDIRECT_URI = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-oauth/callback`;

type GoogleAccount = {
  email: string;
  status: string;
  last_sync_at: string | null;
  connected_at: string;
  scopes: string[] | null;
};

async function fetchGoogleAccounts(): Promise<GoogleAccount[]> {
  const { data, error } = await supabase
    .from('google_accounts')
    .select('email, status, last_sync_at, connected_at, scopes')
    .eq('status', 'connected')
    .order('connected_at', { ascending: true });
  if (error) throw error;
  return (data as GoogleAccount[]) ?? [];
}

/** True when this inbox has NOT granted the gmail.send scope (can capture + draft
 *  but can't send a reply yet — a one-click Re-authorize fixes it). */
function needsSendScope(a: GoogleAccount): boolean {
  return !(a.scopes ?? []).some((s) => s.endsWith('/gmail.send'));
}

/** True when Google has invalidated the stored refresh token, so nothing can
 *  sync until the owner re-consents.
 *
 *  Why this exists: `invalid_grant` is Google's response once a refresh token is
 *  revoked or expired — and Google expires refresh tokens after 7 days for any
 *  OAuth app still in "Testing" publishing status, which is where a
 *  bring-your-own-credentials setup lives. On 2026-08-17 all three of the
 *  owner's inboxes had been dead for up to 22 days and the UI offered no way
 *  back: the Re-authorize button was gated ONLY on a missing send scope, which
 *  these inboxes already had. The fix was one click away and unreachable. */
function needsReconnect(row: ConnectionRow | undefined): boolean {
  const err = (row?.last_error ?? '').toLowerCase();
  return row?.status === 'error' || err.includes('invalid_grant') || err.includes('token');
}

async function invokeGoogleReauth(email: string): Promise<void> {
  // Re-runs Google consent using the inbox's already-stored client creds — no
  // disconnect, no JSON re-upload. startOAuth spreads `extra` last, so this
  // overrides action to 'reauth'.
  await startOAuth('google-oauth', { action: 'reauth', email });
}

async function invokeGoogleDisconnect(email: string): Promise<void> {
  const { error } = await supabase.functions.invoke('google-oauth', {
    body: { action: 'disconnect', email },
  });
  if (error) {
    let message = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        message = parsed?.message ?? parsed?.error ?? message;
      }
    } catch {
      /* fall back */
    }
    throw new Error(message);
  }
}

// Parse a downloaded Google OAuth client JSON. Google wraps a Web-application
// client as { web: { client_id, client_secret, … } } (installed apps use
// `installed`); we also accept a bare { client_id, client_secret }.
function parseGoogleClientJson(text: string): { client_id: string; client_secret: string } | null {
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const creds = (parsed.web ?? parsed.installed ?? parsed) as Record<string, unknown>;
    const clientId = typeof creds.client_id === 'string' ? creds.client_id : '';
    const clientSecret = typeof creds.client_secret === 'string' ? creds.client_secret : '';
    if (!clientId || !clientSecret) return null;
    return { client_id: clientId, client_secret: clientSecret };
  } catch {
    return null;
  }
}

function lastSyncLabel(row: ConnectionRow | undefined): string | null {
  if (!row?.last_sync_at) return null;
  const d = new Date(row.last_sync_at);
  return `Last synced ${d.toLocaleString()}`;
}

const Connections = (): JSX.Element => {
  const qc = useQueryClient();
  const connQ = useQuery({
    queryKey: ['connections'],
    queryFn: fetchConnections,
    // A WhatsApp linking QR lives only ~20s before the bridge rotates it. At a
    // flat 15s poll the code on screen was routinely 15s old — leaving ~5s to
    // scan, so linking failed with "check connection and try again" unless the
    // owner happened to catch a fresh one. While a QR is pending we poll fast
    // so the displayed code is always near-live; otherwise we stay at 15s so
    // the page isn't hammering the database for no reason.
    refetchInterval: (query) =>
      (query.state.data as Record<string, ConnectionRow> | undefined)?.bridge?.metadata?.whatsapp_qr
        ? 2_000
        : 15_000,
  });

  // Surface the result of an OAuth round-trip (google-oauth callback redirects
  // back here with ?<provider>=connected|error). Then scrub the query string.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let touched = false;
    for (const provider of ['google', 'discord', 'gusto']) {
      const result = params.get(provider);
      if (!result) continue;
      touched = true;
      const name = PROVIDERS.find((p) => p.provider === provider)?.name ?? provider;
      if (result === 'connected') {
        toast.success(`${name} connected. Syncing…`);
        qc.invalidateQueries({ queryKey: ['connections'] });
      } else {
        toast.error(
          `${name} connection failed${params.get('reason') ? `: ${params.get('reason')}` : ''}.`,
        );
      }
    }
    if (touched) window.history.replaceState({}, '', window.location.pathname);
  }, [qc]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-3xl"
    >
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Plug className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Connections</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect your data sources. Credentials are validated and stored encrypted in Supabase
          Vault — they never touch the browser or the database.
        </p>
      </div>

      {connQ.isError && (
        <GlassCard hover={false} className="neo-alert mb-4">
          <p className="text-sm text-alert">
            Failed to load connections: {(connQ.error as Error).message}
          </p>
        </GlassCard>
      )}

      <SetupChecklist
        connectedProviders={
          new Set(
            Object.values(connQ.data ?? {})
              .filter((r) => r.status === 'connected')
              .map((r) => r.provider),
          )
        }
      />

      <div className="space-y-4">
        {PROVIDERS.map((meta) =>
          meta.provider === 'bridge' ? (
            <BridgeTile key="bridge" row={connQ.data?.bridge} />
          ) : meta.provider === 'google' ? (
            <GoogleConnectionCard
              key="google"
              meta={meta}
              row={connQ.data?.google}
              onChanged={() => qc.invalidateQueries({ queryKey: ['connections'] })}
            />
          ) : meta.provider === 'discord' ? (
            <DiscordTile key="discord" meta={meta} row={connQ.data?.discord} />
          ) : (
            <ProviderTile
              key={meta.provider}
              meta={meta}
              row={connQ.data?.[meta.provider]}
              onChanged={() => qc.invalidateQueries({ queryKey: ['connections'] })}
            />
          ),
        )}
      </div>
    </motion.div>
  );
};

// Discord is "connected" in two independent ways, and this deployment uses the
// first: (a) the bot is configured via env (DISCORD_BOT_TOKEN/GUILD/…) and runs
// as the workers_discord_bot daemon — no `connections` row is ever written; or
// (b) the owner runs the self-serve OAuth install (discord-oauth), which writes
// a provider='discord' connected row. The generic ProviderTile only knew about
// (b), so an env-configured, live-and-posting bot showed a misleading "Not
// connected". Mirror BridgeTile: derive status from the daemon heartbeat, and
// treat Discord as connected when the bot heartbeat is alive OR a connected row
// exists. Keep the OAuth "Connect" affordance only while it is not yet live.
function DiscordTile({
  meta,
  row,
}: {
  meta: ProviderMeta;
  row: ConnectionRow | undefined;
}): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const [showSteps, setShowSteps] = useState(false);

  const hbQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: () => readAllHeartbeats(supabase),
    refetchInterval: 10_000,
    retry: 0,
  });
  const botPayload = hbQ.data?.find((x) => x.daemon_name === 'workers_discord_bot')?.payload as
    | HeartbeatPayload
    | null
    | undefined;
  const botAlive = botPayload ? classifyHeartbeat(botPayload, now).status === 'alive' : false;
  const connected = row?.status === 'connected' || botAlive;

  const oauthStart = useMutation({
    mutationFn: () => startOAuth('discord-oauth'),
    onError: (err: Error) => toast.error(err.message),
  });

  const Icon = meta.icon;
  const label = row?.account_label ?? 'Posting approvals + digests to your server';

  return (
    <GlassCard hover={false}>
      <div className="flex items-start gap-4">
        <div className="neo-chip w-10 h-10 rounded-lg flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-foreground" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">{meta.name}</h2>
            {connected ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-primary">
                <PulseDot color="green" size="sm" /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <PulseDot color="amber" size="sm" /> Not connected
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{meta.blurb}</p>

          {connected ? (
            <p className="mt-1 text-[11px] text-muted-foreground">{label}</p>
          ) : (
            meta.steps &&
            meta.steps.length > 0 && (
              <div className="mt-1.5">
                <button
                  type="button"
                  onClick={() => setShowSteps((v) => !v)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                  aria-expanded={showSteps}
                >
                  {showSteps ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                  How to connect
                </button>
                {showSteps && (
                  <ol className="mt-1.5 ml-1 space-y-1.5 border-l border-border pl-3">
                    {meta.steps.map((step, i) => (
                      <li key={i} className="flex gap-2 text-[11px] text-muted-foreground">
                        <span className="shrink-0 font-semibold text-foreground">{i + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )
          )}
        </div>

        <div className="shrink-0 flex flex-col gap-2">
          {!connected && (
            <Button
              type="button"
              size="sm"
              onClick={() => oauthStart.mutate()}
              disabled={oauthStart.isPending}
            >
              {oauthStart.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Connect
            </Button>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

function ProviderTile({
  meta,
  row,
  onChanged,
}: {
  meta: ProviderMeta;
  row: ConnectionRow | undefined;
  onChanged: () => void;
}): JSX.Element {
  const Icon = meta.icon;
  const connected = row?.status === 'connected';
  const wired = Boolean(meta.connect);
  const pasteConnect = meta.connect?.type === 'paste' ? meta.connect : null;
  const oauthConnect = meta.connect?.type === 'oauth' ? meta.connect : null;
  // paste connect renders a single-input form before connecting.
  const inputConnect = pasteConnect;
  const [showConnect, setShowConnect] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [secret, setSecret] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: async (): Promise<{ account?: string | null }> => {
      const value = secret.trim();
      if (!value) throw new Error(`${inputConnect?.label ?? 'Value'} is required.`);
      return invokeConnections({ action: 'connect', provider: meta.provider, secret: value });
    },
    onSuccess: (res) => {
      // Domain connect redirects the browser, so this only fully fires for paste.
      toast.success(`${meta.name} connected${res.account ? ` as ${res.account}` : ''}. Syncing…`);
      setShowConnect(false);
      setSecret('');
      setFormError(null);
      onChanged();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const oauthStart = useMutation({
    mutationFn: () => startOAuth(oauthConnect?.fn ?? ''),
    onError: (err: Error) => toast.error(err.message),
  });

  const sync = useMutation({
    mutationFn: () => invokeConnections({ action: 'sync', provider: meta.provider }),
    onSuccess: () => {
      toast.success(`${meta.name} sync queued.`);
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const disconnect = useMutation({
    mutationFn: () => invokeConnections({ action: 'disconnect', provider: meta.provider }),
    onSuccess: () => {
      toast.success(`${meta.name} disconnected.`);
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <GlassCard hover={false}>
      <div className="flex items-start gap-4">
        <div className="neo-chip w-10 h-10 rounded-lg flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-foreground" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">{meta.name}</h2>
            {connected ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-primary">
                <PulseDot color="green" size="sm" /> Connected
              </span>
            ) : wired ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <PulseDot color="amber" size="sm" /> Not connected
              </span>
            ) : (
              <span className="neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full text-muted-foreground">
                Coming soon
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{meta.blurb}</p>

          {/* "How to connect" walkthrough — collapsible, for not-yet-connected wired providers */}
          {meta.steps && meta.steps.length > 0 && !connected && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={() => setShowSteps((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                aria-expanded={showSteps}
              >
                {showSteps ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
                How to connect
              </button>
              {showSteps && (
                <ol className="mt-1.5 ml-1 space-y-1.5 border-l border-border pl-3">
                  {meta.steps.map((step, i) => (
                    <li key={i} className="flex gap-2 text-[11px] text-muted-foreground">
                      <span className="shrink-0 font-semibold text-foreground">{i + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {connected && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              {row?.account_label ? `${row.account_label} · ` : ''}
              {lastSyncLabel(row) ?? meta.connectedHint ?? 'Sync pending…'}
            </p>
          )}
          {row?.status === 'error' && row.last_error && (
            <p className="mt-1 text-[11px] text-alert truncate">{row.last_error}</p>
          )}

          {/* Input connect form: paste-key (Fathom) */}
          {inputConnect && !connected && showConnect && (
            <div className="mt-3">
              <Label
                htmlFor={`secret-${meta.provider}`}
                className="text-[10px] uppercase tracking-wide"
              >
                {inputConnect.label}
              </Label>
              <Input
                id={`secret-${meta.provider}`}
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={inputConnect.placeholder}
                className="mt-1 font-mono text-sm"
                autoComplete="off"
              />
              {inputConnect.help && (
                <a
                  href={inputConnect.help.href}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  {inputConnect.help.text}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {formError && (
                <p className="mt-2 text-xs text-alert" role="alert">
                  {formError}
                </p>
              )}
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowConnect(false);
                    setFormError(null);
                  }}
                  disabled={connect.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => connect.mutate()}
                  disabled={connect.isPending}
                >
                  {connect.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Connect
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Right-side actions */}
        <div className="shrink-0 flex flex-col gap-2">
          {wired && !connected && !showConnect && (
            <Button
              type="button"
              size="sm"
              onClick={() => (oauthConnect ? oauthStart.mutate() : setShowConnect(true))}
              disabled={oauthStart.isPending}
            >
              {oauthStart.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Connect
            </Button>
          )}
          {connected && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
              >
                {sync.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                )}
                Sync now
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
                className="text-muted-foreground hover:text-alert"
              >
                Disconnect
              </Button>
            </>
          )}
          {!wired && <Check className="w-4 h-4 text-muted-foreground/30" />}
        </div>
      </div>
    </GlassCard>
  );
}

// The exhaustive, button-by-button GCP setup — matched to today's console
// ("Google Auth Platform", not the old "OAuth consent screen" location).
export function GoogleSetupGuide(): JSX.Element {
  const [copied, setCopied] = useState(false);
  const copyRedirect = (): void => {
    navigator.clipboard.writeText(GOOGLE_REDIRECT_URI).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => toast.error('Could not copy — select and copy it manually.'),
    );
  };
  const link = (href: string, text: string): JSX.Element => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-primary hover:underline inline-flex items-center gap-0.5"
    >
      {text}
      <ExternalLink className="w-3 h-3" />
    </a>
  );
  return (
    <div className="neo-inset mt-3 rounded-lg p-4 text-sm text-muted-foreground">
      <p className="text-foreground font-medium">
        Each inbox uses its own Google Cloud OAuth app — a one-time ~2-minute setup per inbox.
        Follow these exactly:
      </p>
      <ol className="mt-3 space-y-2.5 list-decimal pl-5 leading-relaxed">
        <li>
          <b>Create a project.</b> Open{' '}
          {link('https://console.cloud.google.com/projectcreate', 'Google Cloud Console')}, give it
          a name (e.g. “Reverra – &lt;inbox&gt;”), click <b>Create</b>, then make sure it’s selected
          in the top bar.
        </li>
        <li>
          <b>Enable two APIs.</b> Go to{' '}
          {link('https://console.cloud.google.com/apis/library', 'APIs & Services › Library')}.
          Search <b>Gmail API</b> → open it → <b>Enable</b>. Then search <b>Google Calendar API</b>{' '}
          → <b>Enable</b>.
        </li>
        <li>
          <b>Configure the consent screen.</b> Open{' '}
          {link('https://console.cloud.google.com/auth/overview', 'Google Auth Platform')} →{' '}
          <b>Get started</b>. Enter an app name + your support email → <b>Next</b> → Audience{' '}
          <b>External</b> → <b>Next</b> → contact email → agree → <b>Create</b>.
        </li>
        <li>
          <b>Add this inbox as a test user.</b> Open{' '}
          {link('https://console.cloud.google.com/auth/audience', 'Audience')} → under{' '}
          <b>Test users</b> click <b>+ Add users</b> → type the inbox’s email address → <b>Save</b>.{' '}
          <span className="text-foreground">This is the step that prevents “Access blocked”.</span>
        </li>
        <li>
          <b>Create the OAuth client.</b> Open{' '}
          {link('https://console.cloud.google.com/auth/clients', 'Clients')} → <b>Create client</b>{' '}
          → Application type <b>Web application</b> → name it → under{' '}
          <b>Authorized redirect URIs</b> click <b>+ Add URI</b> and paste this <em>exact</em> URL:
          <div className="neo-inset mt-1.5 flex items-center gap-2 rounded-md px-2.5 py-1.5">
            <code className="flex-1 text-[11px] text-foreground break-all">
              {GOOGLE_REDIRECT_URI}
            </code>
            <button
              type="button"
              onClick={copyRedirect}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          Then click <b>Create</b>.
        </li>
        <li>
          <b>Download the credentials.</b> In the “OAuth client created” dialog, click{' '}
          <b>Download JSON</b>.
        </li>
        <li>
          <b>Link it below.</b> Upload that JSON (or paste the Client ID + secret), click{' '}
          <b>Connect</b>, and choose this inbox in Google’s account picker.
        </li>
      </ol>
      <p className="mt-3 text-xs">
        To <b>revoke</b> an inbox later, click <b>Disconnect</b> — Reverra revokes the token at
        Google and deletes it here. To add another inbox, repeat these steps with its own project.
      </p>
    </div>
  );
}

export function GoogleConnectionCard({
  meta,
  row,
  onChanged,
}: {
  meta: ProviderMeta;
  row: ConnectionRow | undefined;
  onChanged: () => void;
}): JSX.Element {
  const Icon = meta.icon;
  const accountsQ = useQuery({
    queryKey: ['google_accounts'],
    queryFn: fetchGoogleAccounts,
    refetchInterval: 15_000,
  });
  const accounts = accountsQ.data ?? [];
  const [showAdd, setShowAdd] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: async (): Promise<void> => {
      const id = clientId.trim();
      const secret = clientSecret.trim();
      if (!id || !secret) {
        throw new Error(
          'Paste your OAuth Client ID + Client secret, or upload the downloaded JSON.',
        );
      }
      // Redirects the browser to Google's account chooser (select_account).
      await startOAuth('google-oauth', { client_id: id, client_secret: secret });
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const disconnect = useMutation({
    mutationFn: (email: string) => invokeGoogleDisconnect(email),
    onSuccess: () => {
      toast.success('Inbox disconnected — access revoked at Google.');
      accountsQ.refetch();
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Re-authorize: reuses stored creds → redirects to Google's consent screen.
  // On error (e.g. no stored creds), surface the message so the owner falls
  // back to the full Add-inbox flow.
  const reauth = useMutation({
    mutationFn: (email: string) => invokeGoogleReauth(email),
    onError: (err: Error) => toast.error(err.message),
  });

  function onFile(file: File): void {
    file
      .text()
      .then((text) => {
        const creds = parseGoogleClientJson(text);
        if (!creds) {
          setFormError(
            'That JSON didn’t contain a client_id + client_secret. Upload the OAuth client JSON you downloaded from Google (step 6).',
          );
          return;
        }
        setClientId(creds.client_id);
        setClientSecret(creds.client_secret);
        setFormError(null);
        toast.success('Credentials loaded from JSON.');
      })
      .catch(() => setFormError('Could not read that file.'));
  }

  return (
    <GlassCard hover={false}>
      <div className="flex items-start gap-3">
        <div className="neo-chip mt-0.5 rounded-lg p-2">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-foreground">{meta.name}</h3>
            {accounts.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-sage">
                <PulseDot color="green" />
                {accounts.length} connected
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{meta.blurb}</p>

          {/* Connected inboxes */}
          {accounts.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {accounts.map((a) => {
                const reconnect = needsReconnect(row);
                // A dead token beats a missing scope: re-authorizing fixes both,
                // but nothing syncs at all until the token is restored.
                const needsSend = !reconnect && needsSendScope(a);
                return (
                  <li
                    key={a.email}
                    className={`neo-inset rounded-md px-3 py-2 ${
                      reconnect
                        ? 'border-l-2 border-alert'
                        : needsSend
                          ? 'border-l-2 border-attention'
                          : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{a.email}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {a.last_sync_at
                            ? `Last synced ${new Date(a.last_sync_at).toLocaleString()}`
                            : 'Connected — first sync pending'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => disconnect.mutate(a.email)}
                        disabled={disconnect.isPending}
                        aria-label={`Disconnect ${a.email}`}
                        title="Disconnect"
                        className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-alert hover:bg-alert/5 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {(reconnect || needsSend) && (
                      <div
                        className={`mt-2 flex flex-wrap items-center justify-between gap-2 border-t pt-2 ${
                          reconnect ? 'border-alert/20' : 'border-attention/20'
                        }`}
                      >
                        <p className={`text-[11px] ${reconnect ? 'text-alert' : 'text-attention'}`}>
                          {reconnect
                            ? 'Google’s access for this inbox has expired, so nothing is syncing. Reconnect to restore it — one click, no re-upload.'
                            : 'Sending isn’t enabled for this inbox yet — re-authorize to grant the send-email permission. No re-upload needed.'}
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={reauth.isPending}
                          onClick={() => reauth.mutate(a.email)}
                          className="shrink-0"
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                          {reauth.isPending
                            ? 'Opening Google…'
                            : reconnect
                              ? 'Reconnect'
                              : 'Re-authorize'}
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Add-inbox affordance */}
          {!showAdd ? (
            <Button
              type="button"
              size="sm"
              variant={accounts.length > 0 ? 'outline' : 'default'}
              className="mt-3"
              onClick={() => setShowAdd(true)}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add a Google inbox
            </Button>
          ) : (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowGuide((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-primary hover:opacity-80"
                aria-expanded={showGuide}
              >
                {showGuide ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
                Step-by-step: create the Google Cloud app
              </button>
              {showGuide && <GoogleSetupGuide />}

              <div className="mt-4 space-y-3">
                <div>
                  <Label className="text-xs">Upload the downloaded client JSON</Label>
                  <label className="neo-inset mt-1 flex items-center gap-2 rounded-md border border-dashed border-border/60 px-3 py-2 text-sm text-muted-foreground cursor-pointer hover:border-primary/50">
                    <Upload className="w-4 h-4" />
                    <span>Choose file…</span>
                    <input
                      type="file"
                      accept="application/json,.json"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onFile(f);
                      }}
                    />
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground text-center">
                  — or paste manually —
                </p>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Client ID</Label>
                    <Input
                      className="mt-1"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      placeholder="…apps.googleusercontent.com"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Client secret</Label>
                    <Input
                      className="mt-1"
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder="GOCSPX-…"
                    />
                  </div>
                </div>
                {formError && <p className="text-xs text-alert">{formError}</p>}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => connect.mutate()}
                    disabled={connect.isPending || !clientId.trim() || !clientSecret.trim()}
                  >
                    {connect.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        Redirecting…
                      </>
                    ) : (
                      'Connect'
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowAdd(false);
                      setFormError(null);
                    }}
                    disabled={connect.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {row?.status === 'error' && row.last_error && (
            <p className="mt-2 text-xs text-alert">{row.last_error}</p>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

export default Connections;
